import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { userKpisQueryOptions } from "@/lib/queries";
import { useKpiCatalog } from "@/features/overview/hooks/useKpiCatalog";
import {
  buildMetricCatalog,
  catalogById,
  type MetricCatalogEntry,
} from "@/features/overview/lib/metricCatalog";
import {
  buildMyBrandKpiCatalog,
  myBrandCatalogById,
  type MyBrandKpiCatalogEntry,
} from "@/features/drilldown/lib/my-brand-kpi-catalog";
import { mergeById, selectorRows } from "../lib/kpi-selector-catalog";

/** The page domains that own per-user KPI settings routes. */
export type KpiSelectorScope = "overview" | "drilldown";

/**
 * KPI data for the page-level pill pickers.
 *
 * The `*Catalog` lists are the caller's own KPI settings — what a picker should
 * offer. The `*ById` maps are org-union-user with user overrides winning, because
 * rendering resolves through them and a user-created KPI has no org row: without
 * the union, selecting one would hit an undefined lookup that `MyBrandKpiPillRow`
 * and `resolveMetrics` both drop silently.
 *
 * Reads `userKpisQueryOptions` directly rather than `useUserKpis`, which also
 * fetches the available-org list that only the drawer's add-popover needs — this
 * hook is mounted by seven components across two pages.
 */
export function useKpiSelectorCatalog(scope: KpiSelectorScope) {
  const tenantId = useAuth((s) => s.activeTenantId) ?? "";
  const org = useKpiCatalog(scope);
  const userQuery = useQuery(userKpisQueryOptions(scope, tenantId));

  // Until the user's settings land, fall back to the org catalog so the chart
  // keeps its pills instead of flashing empty on a slow or failed request.
  const userRows = useMemo(
    () => (userQuery.data ? selectorRows(userQuery.data) : null),
    [userQuery.data],
  );

  const metricCatalog: MetricCatalogEntry[] = useMemo(
    () => (userRows ? buildMetricCatalog(userRows) : org.metricCatalog),
    [userRows, org.metricCatalog],
  );

  const myBrandCatalog: MyBrandKpiCatalogEntry[] = useMemo(
    () => (userRows ? buildMyBrandKpiCatalog(userRows) : org.myBrandCatalog),
    [userRows, org.myBrandCatalog],
  );

  const metricById = useMemo(
    () => mergeById(org.metricById, catalogById(metricCatalog)),
    [org.metricById, metricCatalog],
  );

  const myBrandById = useMemo(
    () => mergeById(org.myBrandById, myBrandCatalogById(myBrandCatalog)),
    [org.myBrandById, myBrandCatalog],
  );

  return {
    metricCatalog,
    metricById,
    myBrandCatalog,
    myBrandById,
    isLoading: org.isLoading || userQuery.isLoading,
  };
}
